'use client';

import { useState, useCallback } from 'react';
import { Twitter } from '@/components/common/Twitter';
import { SidebarAd } from '@/tracking/ads/components/SidebarAd';
import { useMustacheTranslation, useOTSTranslation } from '@/i18n/useOTSTranslation';
import { RouteAndAnalyticsProps } from '@/page-components/pathTypes';
import { Routes } from '@/routes';
import { SnowReportComments } from '@/components/resort/SnowReportComments';
import {
  IRelatedNews,
  IResortPageInfo,
  IResortPageSnowInfo,
  IResortPathInfo,
  IDescriptionsResort,
  IResortTrailmapPage,
  IReviewsGeneral,
  IWeatherInfo,
  OpenFlag,
  IFirstHandReport,
} from '@/api/resort/types';
import { INearbyResort, IWebcamItem } from '@/api/region/types';
import { Main } from '@/components/resort/Layout/Main';
import { Side } from '@/components/resort/Layout/Side';
import PlanYourTrip from '@/components/common/PlanYourTrip/ResortWidget';
import RelatedArticles from '@/components/resort/RelatedArticles';
import { RegularAd } from '@/tracking/ads/components/RegularAd';
import { Hero400Error } from '@/components/400';
import { SHORT_DATE_FORMAT, formatDateFromStr } from '@/util/formatDate';
import { useDistanceFormatter } from '@/i18n/unitFormat';
import { useSanitizedHtmlProps } from '@/util/sanitized';
import { PopupFeedback } from '@/components/resort/PopupFeedback';
import { MessageSquare } from 'lucide-react';
import ElfsightInstagramWidget from '@/components/resort/ElfsightInstagramWidget/Index';
import { NearbyResorts } from '@/components/resort/NearbyResorts';
import { SkiGptPromotion } from '@/components/common/SkiGptPromotion';
import { PageBody } from '@/components/common/PageBody';
import { SeoSection } from '@/components/common/SeoSection';
import { ClosedBanner } from '@/page-components/resort/skireport/atoms';
import { SnowfallRow } from '@/page-components/resort/skireport/SnowfallRow';
import { getRecentSnowfallFreshness } from '@/util/recentSnowfallFreshness';
import { SnowDepth } from '@/page-components/resort/skireport/SnowDepth';
import { LiftsTerrain } from '@/page-components/resort/skireport/LiftsTerrain';
import { FhrAndWebcam } from '@/page-components/resort/skireport/FhrAndWebcam';
import { ResortOverview } from '@/page-components/resort/skireport/ResortOverview';
import { IResortLayout } from './_layout';

export type ResortSkiReportPageProps = {
  featured: IWebcamItem | null,
  resort: IResortPageInfo,
  fullResort: IResortPageSnowInfo,
  pathInfo: IResortPathInfo,
  reviewsGeneral: IReviewsGeneral | null,
  resortDescriptionTabs: Array<IDescriptionsResort>,
  trailmap: IResortTrailmapPage | null,
  weatherInfo: IWeatherInfo | null,
  relatedNews: IRelatedNews | null,
  firstHand: IFirstHandReport | null,
  nearbyResorts: INearbyResort[],
} & RouteAndAnalyticsProps & IResortLayout;

export const DEBUG_TAG = 'resort-skireport-page';
export const TEMPLATE_ID = '2.2.1';
export const TEMPLATE_NAME = 'Resort Ski Report';

// Open-state derivation matches the prior production rule so the
// closed-state banner only kicks in for fully closed / out-of-season
// resorts (Open, WeekendsOnly, TemporarilyClosed, NoReportAvailable
// all count as "open enough" for live conditions).
const OPEN_FLAGS = new Set<OpenFlag>([
  OpenFlag.Open,
  OpenFlag.WeekendsOnly,
  OpenFlag.TemporarilyClosed,
  OpenFlag.NoReportAvailable,
]);

export default function ResortSkiReportPage({
  featured,
  fullResort,
  pathInfo,
  firstHand,
  resortDescriptionTabs,
  relatedNews,
  resort,
  trailmap,
  weatherInfo,
  nearbyResorts,
}: ResortSkiReportPageProps) {
  const [isOpenPopup, toggleIsOpenPopup] = useState<boolean>(false);

  const togglePopup = useCallback(() => {
    toggleIsOpenPopup(!isOpenPopup);
  }, [isOpenPopup]);

  const { t } = useOTSTranslation('common');
  const m = useMustacheTranslation('common');
  const dt = useDistanceFormatter('cmOrInchShort');

  if (!fullResort) {
    return <Hero400Error light />;
  }

  const isOpen = !!fullResort.status.openFlag && OPEN_FLAGS.has(fullResort.status.openFlag);

  // Snowfall windows: last 5 reported days, next 5 forecast days.
  const recent = (fullResort.recent ?? []).slice(-5);
  const forecast = (fullResort.forecast ?? []).slice(0, 5);
  // Staleness of the recent snowfall report — drives fresh / stale-caveat /
  // expired-empty rendering in SnowfallRow.
  const recentFreshness = getRecentSnowfallFreshness(fullResort.updatedAt, fullResort.recent);
  const recentReportedAtLabel = recentFreshness.reportedAt?.toFormat(SHORT_DATE_FORMAT) ?? '';
  const weatherDays = weatherInfo?.weatherItems?.slice(0, 5) ?? [];

  // Resort overview prose (matches what ski-resort.tsx pulls).
  const resortOverviewText = resortDescriptionTabs.find((d) => d.tab === 'resort_profile')?.description ?? '';
  const overviewSanitized = useSanitizedHtmlProps(resortOverviewText);
  // eslint-disable-next-line no-underscore-dangle
  const overviewPlain = overviewSanitized.dangerouslySetInnerHTML.__html.replace(/<[^>]+>/g, '').trim();

  const instagram = resort.socialMediaAccounts.find((acc) => acc.type === 'instagram');

  const projectedOpening = fullResort.status.openingDate
    ? formatDateFromStr(fullResort.status.openingDate, SHORT_DATE_FORMAT) : null;
  const projectedClosing = fullResort.status.closingDate
    ? formatDateFromStr(fullResort.status.closingDate, SHORT_DATE_FORMAT) : null;

  const overviewHref = Routes.resortTripPlanningOverview(pathInfo);
  const allCamsHref = Routes.resortCams(pathInfo);
  const allFhrHref = Routes.resortFHReports(pathInfo);
  const fullWeatherHref = Routes.resortWeather(pathInfo);
  const snowHistoryHref = Routes.resortSnowfall(pathInfo);

  const reportedAt = formatDateFromStr(fullResort.updatedAt, SHORT_DATE_FORMAT);

  return (
    <>
      {isOpenPopup && (
        <PopupFeedback
          togglePopup={togglePopup}
          resort={fullResort.title}
          id={fullResort.uuid}
        />
      )}
      <Main>
        <PageBody>
          <SeoSection
            title={t('resort.skiReport.seoTitle', { resort: fullResort.title })}
          >
            {reportedAt && (
              <>
                <span className="font-semibold text-foreground">
                  {`${t('resort.skiReport.lastUpdated')} ${reportedAt}.`}
                </span>
                {' '}
              </>
            )}
            {m('resort.skiReport.seoText', {
              resort: fullResort.title,
              depth: dt([fullResort.snow?.base || fullResort.snow?.summit], '-'),
              base_depth_cm: dt(fullResort.snow?.base),
              top_depth_cm: dt(fullResort.snow?.summit),
              base_depth: dt(fullResort.snow?.base),
              top_depth: dt(fullResort.snow?.summit),
              unitMeasurement: '',
              lifts_open: fullResort.lifts?.open,
              num_lifts: fullResort.lifts?.total,
              created_dt: reportedAt,
            })}
          </SeoSection>

          {!isOpen && (
            <ClosedBanner
              closedLabel={t('resort.skiReport.closed')}
              resortName={fullResort.title}
              projectedOpening={projectedOpening}
            />
          )}

          <SnowfallRow
            recent={recent}
            forecast={forecast}
            weatherDays={weatherDays}
            snowHistoryHref={snowHistoryHref}
            fullWeatherHref={fullWeatherHref}
            state={recentFreshness.state}
            reportedAtLabel={recentReportedAtLabel}
          />

          {isOpen && (
            <SnowDepth fullResort={fullResort} trailmap={trailmap} />
          )}

          {fullResort.comment && <SnowReportComments comments={fullResort.comment} />}

          {isOpen && <LiftsTerrain fullResort={fullResort} />}

          {/* Subtle "Notice a problem?" line — sits just above the
              SkiGPT widget so the feedback affordance is unmistakable
              but doesn't compete with primary content. */}
          <div className="-mb-6 flex justify-end">
            <button
              type="button"
              onClick={togglePopup}
              className={[
                'inline-flex items-center gap-1.5 rounded-full',
                'border border-border bg-background px-3 py-1',
                'text-xs font-medium text-muted-foreground transition',
                'hover:border-primary/40 hover:text-secondary',
              ].join(' ')}
            >
              <MessageSquare className="h-3 w-3 fill-none" aria-hidden />
              {t('resort.skiReport.provideFeedback')}
            </button>
          </div>

          <SkiGptPromotion
            templateId={TEMPLATE_ID}
            name={fullResort.title}
          />

          <RegularAd isFirst />

          <FhrAndWebcam
            firstHand={firstHand}
            featured={featured}
            allFhrHref={allFhrHref}
            allCamsHref={allCamsHref}
          />

          <ResortOverview
            fullResort={fullResort}
            overviewPlain={overviewPlain}
            overviewHref={overviewHref}
            projectedOpening={projectedOpening}
            projectedClosing={projectedClosing}
          />

          <RegularAd />

          <div id="wisepops-embed" />

          <PlanYourTrip
            resort={pathInfo}
            hasSkiRental={pathInfo.hasSkiRental}
            hasFlights={pathInfo.hasFlights}
            hasCarRental={pathInfo.hasCarRental}
            liftsUrl={fullResort.liftsUrl}
            rentalUrl={fullResort.rentalUrl}
            lessonsUrl={fullResort.lessonsUrl}
            isPartner={fullResort.isPartner}
            resortCountry={fullResort?.region?.country_code}
          />

          {nearbyResorts && (
            <NearbyResorts nearbyResorts={nearbyResorts.slice(1)} />
          )}
        </PageBody>
      </Main>

      <Side>
        {Boolean(!!instagram && fullResort?.isPartner) && (
          <ElfsightInstagramWidget uuid={fullResort.uuid} />
        )}
        {Boolean(instagram === undefined && fullResort.twitter) && (
          <Twitter feed={fullResort.twitter ?? ''} />
        )}
        <RelatedArticles
          relatedNewsList={relatedNews}
        />
        <SidebarAd id={pathInfo.uuid} showWeSki={false} />
      </Side>

      {/* JSON-LD schemas render server-side from the RSC page (see
          app/[region]/[resort]/skireport/page.tsx). Keeping them out of
          this 'use client' tree means the schema builders + their i18n
          calls never enter the client bundle. */}
    </>
  );
}

// fetchProps moved to ./skireport.server.ts so the App Router RSC page can
// import the data fetcher without pulling in this 'use client' bundle.
